import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useUser, useAuth } from './lib/auth';
import { accountApi } from './lib/api';
import { log } from '@/lib/logger';
import {
  shouldResetOnboardingTest,
  ONBOARDING_TEST_GUARD_KEY,
} from './lib/onboardingTestReset';
import { STORAGE_KEY as ONBOARDING_STORAGE_KEY } from './components/onboarding/OnboardingWizard';
import { identify } from './lib/analytics';
import { setSentryUser } from './lib/sentry';
import { useAppSession } from './hooks/useAppSession';
import { useDashboardAuthTelemetry } from './hooks/useDashboardAuthTelemetry';
import { useExtensionAuthBridge } from './hooks/useExtensionAuthBridge';
import { useFreeScanClaim } from './hooks/useFreeScanClaim';
import { useSiteAuditClaim } from './hooks/useSiteAuditClaim';
import { useSessionExpiredRedirect } from './hooks/useSessionExpiredRedirect';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProjectsPage } from './pages/ProjectsPage';
import { IssuesPage } from './pages/IssuesPage';
import { IssueDetailPage } from './pages/TicketDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { FramerReviewPage } from './pages/FramerReviewPage';
import { SchedulePage } from './pages/SchedulePage';
import { ReportsPage } from './pages/ReportsPage';
import { ExtensionAuthPage } from './pages/ExtensionAuthPage';
import { FigmaConnectedPage } from './pages/FigmaConnectedPage';
import { IntegrationConnectedPage } from './pages/IntegrationConnectedPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { BillingPage } from './pages/BillingPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { ExtensionSessionPage } from './pages/ExtensionSessionPage';
import { DashboardPage } from './pages/DashboardPage';
import { InvitePage } from './pages/InvitePage';
import { BetaInvitePage } from './pages/BetaInvitePage';
import { ProjectSettingsRedirect } from './components/projects/settings/ProjectSettingsRedirect';
import McpAuthorizePage from './pages/McpAuthorizePage';
import McpConnectPage from './pages/McpConnectPage';
import { ReviewHandoffPage } from './pages/ReviewHandoffPage';
import { OpenSitePage } from './pages/OpenSitePage';
import { DesignSystemPage } from './pages/DesignSystemPage';
import { isDevBuild } from './lib/devBuild';

function TicketRedirect() {
  const { ticketId } = useParams();
  return <Navigate to={`/issues/${ticketId}`} replace />;
}

function useAnalyticsIdentify() {
  const { user } = useUser();
  const { data: session } = useAppSession();
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !session) return;
    // Use the internal DB userId (UUID) from the bootstrap response, NOT
    // the Clerk user.id. The extension identifies with the same internal ID,
    // so PostHog sees both surfaces as the same person.
    const internalId = session.user.id;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email || identifiedRef.current === internalId) return;
    identifiedRef.current = internalId;
    identify(internalId, email, user.fullName ?? undefined);
    setSentryUser({ id: internalId, email });
  }, [user, session]);
}


/**
 * Dev-only: when the dedicated onboarding test account signs in, wipe its data
 * and drop it back into onboarding so the new-user flow can be tested on every
 * fresh sign-in. No-op in the production build (gated on import.meta.env.DEV)
 * and for every other account. See ./lib/onboardingTestReset for the rationale.
 */
function useOnboardingTestReset() {
  const { user } = useUser();
  // useAuth() is Clerk's hook at runtime; sessionId isn't in the narrowed
  // wrapper type, so read it through a widened view (undefined under the
  // auth-disabled stub, which the guard treats as "no session" -> no-op).
  const auth = useAuth() as { getToken: () => Promise<string | null>; sessionId?: string | null };
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    const email = user?.primaryEmailAddress?.emailAddress;
    const sessionId = auth.sessionId ?? null;

    let lastHandled: string | null = null;
    try {
      lastHandled = localStorage.getItem(ONBOARDING_TEST_GUARD_KEY);
    } catch {
      // localStorage can throw in private mode — treat as "never handled".
    }

    if (
      !shouldResetOnboardingTest({
        isDev: import.meta.env.DEV,
        email,
        sessionId,
        lastHandledSessionId: lastHandled,
      })
    ) {
      return;
    }

    firedRef.current = true;
    // Mark this session BEFORE the async reset so the post-reload render (same
    // session id) sees lastHandled === sessionId and does not loop.
    try {
      localStorage.setItem(ONBOARDING_TEST_GUARD_KEY, sessionId as string);
    } catch {
      // ignore — private mode
    }

    void (async () => {
      log.info('[onboarding-test-reset] fresh sign-in for the test account — wiping + restarting onboarding');
      try {
        const token = await auth.getToken();
        await accountApi.reset(token ?? undefined);
        // Mirror DevResetButton's cleanup so the wizard + extension start clean.
        try {
          sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
        } catch {
          // ignore
        }
        try {
          sessionStorage.setItem('oqa_extension_reset_pending', '1');
        } catch {
          // ignore
        }
        window.location.assign('/onboarding');
      } catch (err) {
        // Keep the guard set (don't roll back) so a persistent failure can't
        // retry-loop. This session is skipped; the next fresh sign-in (new
        // session id) retries, and the manual dev reset button stays available.
        log.error('[onboarding-test-reset] reset failed — skipping this session', { err });
      }
    })();
  }, [user, auth]);
}

export function App() {
  useDashboardAuthTelemetry();
  useAnalyticsIdentify();
  useOnboardingTestReset();
  useExtensionAuthBridge();
  // Attach an anonymous free-tool session to the just-created account (OVE-4).
  // App-level so it survives the new-signup onboarding redirect.
  useFreeScanClaim();
  useSiteAuditClaim();
  useSessionExpiredRedirect();

  return (
    <Routes>
      {/* Dev-only design-system gallery (kitchen sink). Unreachable in prod. */}
      {isDevBuild() && <Route path="/design-system" element={<DesignSystemPage />} />}

      {/* Auth routes */}
      <Route path="/auth/callback/*" element={<AuthCallbackPage />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/extension-auth/*" element={<ExtensionAuthPage />} />
      <Route path="/figma-connected" element={<FigmaConnectedPage />} />
      <Route path="/integration-connected" element={<IntegrationConnectedPage />} />

      {/* Team invite acceptance — public so signed-out users can land here
          from the email and then sign in/up before accepting */}
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* Beta invite redemption — outreach prospects land here from cold emails */}
      <Route path="/beta/:slug" element={<BetaInvitePage />} />

      {/* Onboarding (protected, outside Layout) */}
      <Route path="/onboarding" element={
        <ProtectedRoute><OnboardingPage /></ProtectedRoute>
      } />
      <Route path="/extension-session" element={<ExtensionSessionPage />} />
      {/* Compatibility for extensions installed before the quiet-session route. */}
      <Route path="/get-started" element={<ExtensionSessionPage />} />

      {/* MCP OAuth authorize page — opened by the MCP client (npx @overlayqa/mcp)
          in the user's browser. Wrapped in ProtectedRoute so Clerk redirects
          unauthenticated users to /sign-in before the page renders. */}
      <Route path="/mcp/authorize" element={
        <ProtectedRoute><McpAuthorizePage /></ProtectedRoute>
      } />
      <Route path="/mcp/connect" element={
        <ProtectedRoute><McpConnectPage /></ProtectedRoute>
      } />

      {/* Review handoff re-mint bounce - the guest team-review render's
          "Sign in or Create an account" link lands here. ProtectedRoute sends a
          signed-out user through /sign-in?redirect_url=... first; the page
          then re-mints a fresh ?s= and redirects back to the review. */}
      <Route path="/review-handoff/:token" element={
        <ProtectedRoute><ReviewHandoffPage /></ProtectedRoute>
      } />
      <Route path="/share-handoff/:token" element={
        <ProtectedRoute><ReviewHandoffPage kind="share" /></ProtectedRoute>
      } />
      {/* The free audit's Sign in link lands here signed in: the audited site opens in team
          review in the user's own workspace (OpenSitePage handles onboarding itself). */}
      <Route path="/open-site" element={
        <ProtectedRoute><OpenSitePage /></ProtectedRoute>
      } />

      {/* Protected routes — bypass onboarding guard so extension users
           can always manage their account, billing, and projects */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId/issues" element={<IssuesPage />} />
        {/* Retired 2026-09-17: Project Settings is a modal on the issues page. */}
        <Route path="/projects/:projectId/settings/*" element={<ProjectSettingsRedirect />} />
        <Route path="/issues/:ticketId" element={<IssueDetailPage />} />
        <Route path="/tickets/:ticketId" element={<TicketRedirect />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrations/framer" element={<FramerReviewPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/account" element={<Navigate to="/settings" replace />} />
      </Route>

      {/* Public recovery page, including when the visitor is signed out. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
